import styled from 'styled-components';

import { CARD_TONES, CASINO_SETTINGS, CHANNEL_CARD_TONE, COLORS, MOBILE } from '../../theme/tokens';
import { Text } from '../Text/Text';
import type { CalloutTone } from './Callout';

type TonePalette = { bg: string; border: string; fg: string };

// Publish constraint: a shared component is lifted onto main-hybrid on its own, so
// it may only compose token groups that ALREADY exist there. There is no dedicated
// CALLOUT group yet (a post-promotion follow-up), so every tone borrows the closest
// existing themed surface: the inset panel surface for info, the settings ok / lock
// pairs for success / warning, and the exhausted channel-card tints for danger.
// Swapping these for a CALLOUT group later is a change to this map only.
export const TONES: Record<CalloutTone, TonePalette> = {
  info: { bg: COLORS.surfaceInset, border: COLORS.panelBorder, fg: COLORS.accent },
  success: { bg: CASINO_SETTINGS.okBg, border: CASINO_SETTINGS.okBorder, fg: CASINO_SETTINGS.okFg },
  warning: { bg: CASINO_SETTINGS.lockBg, border: CASINO_SETTINGS.lockBorder, fg: CASINO_SETTINGS.lockFg },
  danger: { bg: CHANNEL_CARD_TONE.exhausted.border, border: CARD_TONES.exhausted.border, fg: COLORS.danger },
};

/** Glyph size; also used to indent the stacked mobile action under the copy. */
export const ICON_PX = 16;
const GAP_PX = 10;

export const Root = styled.div<{ $tone: CalloutTone }>`
  display: flex;
  align-items: flex-start;
  gap: ${GAP_PX}px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid ${(p) => TONES[p.$tone].border};
  background: ${(p) => TONES[p.$tone].bg};

  ${MOBILE} {
    flex-wrap: wrap;
  }
`;

// The glyph inherits the tone foreground through currentColor (Icon's default),
// so no colour token has to travel through the .tsx.
export const IconWrap = styled.span<{ $tone: CalloutTone }>`
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: ${ICON_PX}px;
  height: ${ICON_PX}px;
  /* Top-aligned with the first copy line (20px line-height around a 16px glyph). */
  margin-top: 2px;
  color: ${(p) => TONES[p.$tone].fg};
`;

export const Copy = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const Title = styled(Text)`
  display: block;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: ${COLORS.textStrong};
`;

export const Body = styled(Text)`
  display: block;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: ${COLORS.text};
`;

// Right-aligned beside the copy on desktop; on mobile it takes its own row below
// the copy, indented past the glyph so it lines up with the text.
export const Action = styled.div`
  flex: 0 0 auto;
  margin-left: auto;
  align-self: center;

  ${MOBILE} {
    flex: 1 0 100%;
    margin-left: 0;
    align-self: stretch;
    padding-left: ${ICON_PX + GAP_PX}px;
  }
`;
